/**
 * Industry-specific landing pages for programmatic niche SEO.
 * Each targets a specific vertical + "website design/development for [industry]".
 */

export const INDUSTRIES = {
  "cleaning-companies": {
    slug: "cleaning-companies",
    en: {
      name: "cleaning companies",
      title: "Website Development for Cleaning Companies · NetoVibe",
      metaDesc:
        "Affordable website development for cleaning companies from €600. Trust-first design, booking-ready contact forms, GDPR-compliant. Delivered by NetoVibe.",
      h1: "Website Development for Cleaning Companies",
      lede: "We built Zemka Gebäudereinigung's site. We know exactly what commercial-cleaning enquirers look at before they call. NetoVibe delivers professional business websites for cleaning and janitorial companies — trust-first, booking-ready, and honest about pricing.",
      benefits: [
        "Trust-first design (photos, uniforms, insurance badges)",
        "Booking-ready contact forms with service dropdowns",
        "SEO targeting 'Gebäudereinigung + your city'",
        "One clear phone-CTA on every mobile screen",
        "5-star testimonial section prewired",
      ],
      caseStudy: "Zemka Gebäudereinigung — from no website to structured enquiries in weeks.",
    },
    de: {
      name: "Reinigungsfirmen",
      title: "Website-Entwicklung für Reinigungsfirmen · NetoVibe",
      metaDesc:
        "Günstige Website-Entwicklung für Reinigungsfirmen ab 600 €. Vertrauenswürdiges Design, Buchungs-Formulare, DSGVO-konform. Von NetoVibe.",
      h1: "Website-Entwicklung für Reinigungsfirmen",
      lede: "Wir haben die Website von Zemka Gebäudereinigung gebaut. Wir wissen genau, worauf gewerbliche Auftraggeber schauen, bevor sie anrufen. NetoVibe erstellt professionelle Firmenwebsites für Reinigungs- und Hausmeisterservices — vertrauensorientiert, buchungs-ready, ehrliche Preise.",
      benefits: [
        "Vertrauens-orientiertes Design (Fotos, Uniformen, Versicherungs-Siegel)",
        "Buchungsformulare mit Leistungs-Auswahl",
        "SEO auf 'Gebäudereinigung + Ihre Stadt'",
        "Klare Telefon-CTA auf jedem Mobilscreen",
        "5-Sterne-Testimonial-Bereich vorbereitet",
      ],
      caseStudy: "Zemka Gebäudereinigung — von keiner Website zu strukturierten Anfragen in Wochen.",
    },
  },
  "restaurants": {
    slug: "restaurants",
    en: {
      name: "restaurants",
      title: "Website Development for Restaurants · NetoVibe",
      metaDesc:
        "Affordable restaurant website development from €600. Menu, reservations, Google Maps, mobile-first. NetoVibe delivers hospitality sites that convert.",
      h1: "Website Development for Restaurants",
      lede: "Restaurant websites live on mobile. Your customers are hungry, moving, and one bad tap from leaving. NetoVibe builds affordable restaurant websites with menu-first layouts, one-tap phone, Google Maps embeds and reservation-ready forms.",
      benefits: [
        "Menu-first mobile layout (no PDF menus)",
        "One-tap call & Google Maps direction",
        "Reservation-ready form or OpenTable embed",
        "Instagram-feed integration on request",
        "German + English menu switching",
      ],
    },
    de: {
      name: "Restaurants",
      title: "Website-Entwicklung für Restaurants · NetoVibe",
      metaDesc:
        "Günstige Restaurant-Websites ab 600 €. Speisekarte, Reservierung, Google Maps, mobile-first. NetoVibe liefert Gastronomie-Sites, die konvertieren.",
      h1: "Website-Entwicklung für Restaurants",
      lede: "Restaurant-Websites leben auf dem Handy. Ihre Gäste sind hungrig, unterwegs und einen falschen Tipp vom Verlassen entfernt. NetoVibe erstellt bezahlbare Restaurant-Websites mit menü-first Layouts, One-Tap-Telefon, Google-Maps-Einbindung und Reservierungs-Formularen.",
      benefits: [
        "Menü-first Mobile-Layout (keine PDF-Speisekarten)",
        "One-Tap-Anruf & Google-Maps-Navigation",
        "Reservierungs-Formular oder OpenTable-Einbindung",
        "Instagram-Feed-Integration auf Wunsch",
        "Deutsch + Englisch Menü-Umschaltung",
      ],
    },
  },
  "law-firms": {
    slug: "law-firms",
    en: {
      name: "law firms",
      title: "Website Development for Law Firms · NetoVibe",
      metaDesc:
        "Affordable law firm website development from €600. Trust-first, GDPR-compliant, RA-Fachanwalt-friendly. NetoVibe delivers institutional-tier design.",
      h1: "Website Development for Law Firms",
      lede: "Law firm sites are read like a first meeting — every word matters. NetoVibe builds institutional-tier websites for solo Anwälte, Boutique-Kanzleien and international RA firms. Serious typography, disciplined layout, quiet confidence.",
      benefits: [
        "Institutional typography, quiet confidence",
        "Team-page templates that scale from 1 → 20 lawyers",
        "Practice-area landing pages with clear jurisdictional signals",
        "GDPR + Berufsordnung-safe contact forms",
        "Bilingual EN/DE with hreflang for cross-border clients",
      ],
    },
    de: {
      name: "Anwaltskanzleien",
      title: "Website-Entwicklung für Anwaltskanzleien · NetoVibe",
      metaDesc:
        "Günstige Website-Entwicklung für Anwaltskanzleien ab 600 €. Vertrauens-orientiert, DSGVO-konform, Berufsordnung-safe. Institutionelles Design.",
      h1: "Website-Entwicklung für Anwaltskanzleien",
      lede: "Kanzleiwebsites werden gelesen wie das erste Mandantengespräch — jedes Wort zählt. NetoVibe erstellt institutionelle Websites für Einzelanwälte, Boutique-Kanzleien und internationale RA-Kanzleien. Seriöse Typografie, disziplinierte Layouts, ruhige Selbstsicherheit.",
      benefits: [
        "Institutionelle Typografie, ruhige Selbstsicherheit",
        "Team-Seiten-Templates von 1 → 20 Anwälten skalierbar",
        "Fachgebiets-Landingpages mit klaren Signalen",
        "DSGVO- + Berufsordnungs-sichere Kontaktformulare",
        "Zweisprachig EN/DE mit hreflang für grenzüberschreitende Mandanten",
      ],
    },
  },
  "medical-practices": {
    slug: "medical-practices",
    en: {
      name: "medical practices",
      title: "Website Development for Medical Practices · NetoVibe",
      metaDesc:
        "Affordable website development for medical practices from €600. Doctolib-ready, GDPR-compliant, patient-first design. NetoVibe delivers trust-tier sites.",
      h1: "Website Development for Medical Practices",
      lede: "Patients scan a practice website in seconds — hours, address, doctor photo, insurance. NetoVibe builds affordable, GDPR-tight websites for Arztpraxen, Zahnärzte and Heilpraktiker that answer those first-seconds questions.",
      benefits: [
        "Patient-first mobile layout (hours + address above the fold)",
        "Doctolib / jameda booking widget compatible",
        "GDPR + HIPAA-mindset by default",
        "Bilingual for international patient clientele",
        "Accessibility (WCAG AA) as a baseline",
      ],
    },
    de: {
      name: "Arztpraxen",
      title: "Website-Entwicklung für Arztpraxen · NetoVibe",
      metaDesc:
        "Günstige Website-Entwicklung für Arztpraxen ab 600 €. Doctolib-ready, DSGVO-konform, patientenorientiert. NetoVibe liefert vertrauenswürdige Praxis-Sites.",
      h1: "Website-Entwicklung für Arztpraxen",
      lede: "Patienten scannen eine Praxis-Website in Sekunden — Öffnungszeiten, Adresse, Arztfoto, Versicherung. NetoVibe erstellt bezahlbare, DSGVO-strenge Websites für Arztpraxen, Zahnärzte und Heilpraktiker, die genau diese Sekunden-Fragen beantworten.",
      benefits: [
        "Patientenorientiertes Mobile-Layout (Öffnungszeiten + Adresse above the fold)",
        "Kompatibel mit Doctolib- / jameda-Buchungs-Widgets",
        "DSGVO + Datenschutz-first",
        "Zweisprachig für internationale Patientenklientel",
        "Barrierefreiheit (WCAG AA) als Standard",
      ],
    },
  },
  "handwerker": {
    slug: "handwerker",
    en: {
      name: "Handwerker & tradespeople",
      title: "Website Development for Handwerker & Tradespeople · NetoVibe",
      metaDesc:
        "Affordable website development for tradespeople from €600. Phone-first, service-list clear, mobile-optimised. NetoVibe delivers Handwerker sites that win jobs.",
      h1: "Website Development for Handwerker & Tradespeople",
      lede: "Handwerker win jobs by phone. Your website's only job is to earn that call. NetoVibe builds affordable, phone-first websites for Installateure, Elektriker, Maler, Dachdecker and every trade in between.",
      benefits: [
        "Phone number visible on every mobile scroll",
        "Service catalogue with per-service pages for local SEO",
        "Reviews & 5-star trust bar prewired",
        "Emergency-service badge & 24/7 messaging support",
        "Handwerkskammer-friendly Impressum",
      ],
    },
    de: {
      name: "Handwerker",
      title: "Website-Entwicklung für Handwerker · NetoVibe",
      metaDesc:
        "Günstige Website-Entwicklung für Handwerker ab 600 €. Telefon-first, klare Leistungsübersicht, mobile-optimiert. Websites, die Aufträge gewinnen.",
      h1: "Website-Entwicklung für Handwerker",
      lede: "Handwerker gewinnen Aufträge übers Telefon. Ihre Website muss nur diesen Anruf verdienen. NetoVibe erstellt bezahlbare, telefon-first Websites für Installateure, Elektriker, Maler, Dachdecker und jedes Gewerk dazwischen.",
      benefits: [
        "Telefonnummer sichtbar auf jedem Mobile-Scroll",
        "Leistungskatalog mit Einzelseiten für lokales SEO",
        "Bewertungen & 5-Sterne-Trust-Bar vorbereitet",
        "Notdienst-Badge & 24/7-Messaging-Support",
        "Handwerkskammer-freundliches Impressum",
      ],
    },
  },
};

export const INDUSTRY_SLUGS = Object.keys(INDUSTRIES);
